import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box, VStack, HStack, Text, SimpleGrid, Spinner, Input, Alert, AlertIcon,
  Heading, Table, Thead, Tr, Th, Tbody, Td, InputGroup, InputLeftElement,
  InputRightElement, IconButton, Tag, TagLabel, Badge, Portal, Button, useToast,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import AnalyticsTimeWindow from "./AnalyticsTimeWindow";
import MetricCard from "./MetricCard";
import { useAnalyticsQuery } from "../../hooks/useAnalyticsQuery";
import { formatMs, formatNumber, formatDate, formatBucketLabel } from "./analyticsFormatters";
import { BASE_URLS } from "../../components/env";
import { enterImpersonation, isImpersonating } from "../../utils/impersonation";
import { supabasePublic } from "../supabaseClient";

function _backendUrl() {
  const override = process.env.REACT_APP_BACKEND_URL;
  if (override && override.trim() !== "") return override;
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1"
    ? BASE_URLS.local.backend
    : BASE_URLS.render.backend;
}

// Auto-bucket per time window so the chart always has a useful number of points.
// 24h → hour (24 pts), 7d/30d → day, 90d → week, all → month.
function bucketFor(tw) {
  if (tw === "24h") return "hour";
  if (tw === "all") return "month";
  if (tw === "90d") return "week";
  return "day"; // 7d, 30d
}

// Label for the MetricCard helpText. "all time" omits the leading "in".
function windowLabel(tw) {
  if (tw === "all") return "all time";
  return `in ${tw}`;
}

export default function IndividualUserAnalytics({
  timeWindow,
  onTimeWindowChange,
  userId: userIdProp,
  onUserIdChange,
  refreshNonce = 0,
}) {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [userIdState, setUserIdState] = useState(null);
  const userId = userIdProp !== undefined ? userIdProp : userIdState;
  const setUserId = (id) => {
    if (onUserIdChange) onUserIdChange(id);
    else setUserIdState(id);
  };
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [menuRect, setMenuRect] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [adminInfo, setAdminInfo] = useState(null);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pub = supabasePublic();
        const { data } = await pub.auth.getSession();
        const sess = data?.session;
        if (!sess?.user) return;
        const { data: row } = await pub
          .from("users")
          .select("id,email,name")
          .eq("id", sess.user.id)
          .single();
        if (!cancelled) {
          setAdminInfo({
            id: sess.user.id,
            email: sess.user.email || row?.email || "",
            name: row?.name || sess.user.user_metadata?.full_name || "",
            adminToken: sess.access_token,
          });
        }
      } catch (_) { /* ignore — button just stays disabled */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${_backendUrl()}/api/admin/signups/`);
        const json = await res.json();
        if (!cancelled) setUsers(json.users || []);
      } catch (e) {
        if (!cancelled) setUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return users;
    return users.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const schema = (u.schema || "").toLowerCase();
      return name.includes(f) || email.includes(f) || schema.includes(f);
    });
  }, [users, filter]);

  useEffect(() => { setHighlight(0); }, [filter]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const updateMenuRect = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setMenuRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuRect();
    const onChange = () => updateMenuRect();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [isOpen, updateMenuRect]);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === userId) || null,
    [users, userId],
  );

  const { data, loading, error } = useAnalyticsQuery({
    tab: "individual",
    timeWindow,
    userId,
    scope: "all",
    enabled: !!userId,
    refreshNonce,
    bucket: bucketFor(timeWindow),
  });

  const summary = data?.summary || {};
  const series = data?.series || [];

  const pickUser = (u) => {
    setUserId(u.id);
    setFilter("");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const clearUser = () => {
    setUserId(null);
    setFilter("");
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
      setIsOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const u = filtered[highlight];
      if (u) pickUser(u);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const displayName = (u) => (u.name || "").trim() || u.email || u.id;

  return (
    <VStack align="stretch" spacing={5}>
      <HStack spacing={3} align="start">
        <Box position="relative" ref={wrapRef} w="420px">
          {selectedUser ? (
            <HStack
              p={2}
              borderWidth={1}
              borderRadius="md"
              bg="blue.50"
              borderColor="blue.200"
              justify="space-between"
            >
              <HStack spacing={2} minW={0}>
                <Tag size="md" colorScheme="blue" variant="solid">
                  <TagLabel>{displayName(selectedUser)}</TagLabel>
                </Tag>
                {selectedUser.email && (
                  <Text fontSize="sm" color="gray.600" isTruncated>
                    {selectedUser.email}
                  </Text>
                )}
                {selectedUser.schema && (
                  <Badge colorScheme="purple" variant="outline">
                    {selectedUser.schema}
                  </Badge>
                )}
              </HStack>
              <HStack spacing={1}>
                <Button
                  size="xs"
                  colorScheme="orange"
                  variant="solid"
                  isDisabled={
                    !selectedUser ||
                    !adminInfo ||
                    isImpersonating() ||
                    selectedUser.id === adminInfo?.id
                  }
                  onClick={async () => {
                    try {
                      // Capture the route the admin came from (without the React
                      // Router basename) so Exit returns to the SAME page that
                      // hosts the New Signups Dashboard modal — could be
                      // /super-admin or /admin-page depending on entry point.
                      const basename = "/NotesAI";
                      const fullPath = window.location.pathname + window.location.search;
                      const originPath = fullPath.startsWith(basename)
                        ? fullPath.slice(basename.length) || "/super-admin"
                        : fullPath;
                      await enterImpersonation({
                        targetId: selectedUser.id,
                        adminInfo,
                        originState: {
                          path: originPath,
                          modal_state: {
                            signupsOpen: true,
                            activeTab: "user-analytics",
                            selectedUserId: selectedUser.id,
                            timeWindow,
                          },
                        },
                      });
                    } catch (e) {
                      toast({
                        title: "Could not enter view-as",
                        description: String(e?.message || e),
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                      });
                    }
                  }}
                >
                  View as user
                </Button>
                <IconButton
                  aria-label="Change user"
                  icon={<CloseIcon boxSize={2.5} />}
                  size="xs"
                  variant="ghost"
                  onClick={clearUser}
                />
              </HStack>
            </HStack>
          ) : (
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                ref={inputRef}
                placeholder={
                  users.length
                    ? `Search ${users.length} users by name, email, or tenant…`
                    : "Loading users…"
                }
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={onKeyDown}
                autoComplete="off"
              />
              {filter && (
                <InputRightElement>
                  <IconButton
                    aria-label="Clear search"
                    icon={<CloseIcon boxSize={2.5} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                      setFilter("");
                      inputRef.current?.focus();
                    }}
                  />
                </InputRightElement>
              )}
            </InputGroup>
          )}

          {!selectedUser && isOpen && menuRect && (
            <Portal>
              <Box
                ref={menuRef}
                position="fixed"
                top={`${menuRect.top}px`}
                left={`${menuRect.left}px`}
                width={`${menuRect.width}px`}
                maxH="320px"
                overflowY="auto"
                bg="white"
                borderWidth={1}
                borderColor="gray.200"
                borderRadius="md"
                boxShadow="lg"
                zIndex={2000}
              >
                {filtered.length === 0 ? (
                  <Box p={3}>
                    <Text fontSize="sm" color="gray.500">
                      No users match “{filter}”.
                    </Text>
                  </Box>
                ) : (
                  filtered.slice(0, 100).map((u, i) => (
                    <Box
                      key={u.id}
                      px={3}
                      py={2}
                      cursor="pointer"
                      bg={i === highlight ? "blue.50" : "white"}
                      _hover={{ bg: "blue.50" }}
                      onMouseEnter={() => setHighlight(i)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickUser(u);
                      }}
                    >
                      <HStack justify="space-between">
                        <Box minW={0}>
                          <Text fontWeight="medium" isTruncated>
                            {displayName(u)}
                          </Text>
                          {u.email && (
                            <Text fontSize="xs" color="gray.500" isTruncated>
                              {u.email}
                            </Text>
                          )}
                        </Box>
                        {u.schema && (
                          <Badge colorScheme="purple" variant="outline" fontSize="xs">
                            {u.schema}
                          </Badge>
                        )}
                      </HStack>
                    </Box>
                  ))
                )}
                {filtered.length > 100 && (
                  <Box px={3} py={2} bg="gray.50">
                    <Text fontSize="xs" color="gray.500">
                      {filtered.length - 100} more — keep typing to narrow.
                    </Text>
                  </Box>
                )}
              </Box>
            </Portal>
          )}
        </Box>

        <AnalyticsTimeWindow value={timeWindow} onChange={onTimeWindowChange} />
      </HStack>

      {!userId && (
        <Box p={6} bg="gray.50" borderRadius="md">
          <Text color="gray.500">Pick a user to see their analytics.</Text>
        </Box>
      )}

      {userId && error && (
        <Alert status="error"><AlertIcon />Failed to load: {String(error.message || error)}</Alert>
      )}

      {userId && loading && !data ? (
        <Box textAlign="center" py={10}><Spinner /></Box>
      ) : userId && data ? (
        <>
          <Box>
            <Heading size="sm">
              {data.user?.name || data.user?.email || userId}
            </Heading>
            <Text fontSize="xs" color="gray.500">
              {data.user?.email} · {data.user?.role || "role unknown"} · last seen {formatDate(summary.last_seen)}
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            <MetricCard label="Login sessions" value={formatNumber(summary.session_count)}
              helpText={`${formatNumber(summary.days_active)} / ${formatNumber(summary.days_in_period)} active days`}
              color="green.500" />
            <MetricCard label="Time spent" value={formatMs(summary.total_session_ms)}
              helpText={`avg ${formatMs(summary.avg_session_ms)} · longest ${formatMs(summary.longest_session_ms)}`}
              color="purple.500" />
            <MetricCard label="Recordings" value={formatNumber(summary.audio_record_count)}
              helpText={`${formatMs(summary.total_audio_ms)} total · avg ${formatMs(summary.avg_audio_ms)}`}
              color="red.500" />
            <MetricCard label="Transcripts" value={formatNumber(summary.transcript_count)} color="teal.500" />
            <MetricCard label="AI queries" value={formatNumber(summary.ai_query_count)} color="orange.500" />
            <MetricCard label="Document edits" value={formatNumber(summary.document_edit_count)} />
            <MetricCard label="Form submits" value={formatNumber(summary.form_submit_count)} />
            <MetricCard label="Peak usage"
              value={summary.peak_hour || data.peak_hour || "—"}
              helpText={data.peak_weekday || ""} />
            <MetricCard label="Schedule rows" value={formatNumber(summary.schedule_rows)}
              helpText={windowLabel(timeWindow)} color="cyan.500"
              breakdown={series
                .filter((b) => (b.schedule_rows || 0) > 0)
                .map((b) => ({
                  label: formatBucketLabel(b.bucket, data.bucket),
                  value: formatNumber(b.schedule_rows),
                }))} />
          </SimpleGrid>

          <Box>
            <Heading size="sm" mb={2}>Time spent</Heading>
            {series.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No activity in this window.</Text>
            ) : (
              <Box h="280px" bg="white" p={2} borderRadius="md" borderWidth={1}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tickFormatter={(v) => formatBucketLabel(v, bucketFor(timeWindow))} />
                    <YAxis tickFormatter={(v) => formatMs(v)} />
                    <Tooltip
                      labelFormatter={(v) => formatBucketLabel(v, bucketFor(timeWindow))}
                      formatter={(v) => formatMs(v)}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="active_ms" stroke="#805AD5" name="Active time" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>

          <Box>
            <Heading size="sm" mb={2}>Schedule rows</Heading>
            {series.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No activity in this window.</Text>
            ) : (
              <Box h="280px" bg="white" p={2} borderRadius="md" borderWidth={1}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tickFormatter={(v) => formatBucketLabel(v, bucketFor(timeWindow))} />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      labelFormatter={(v) => formatBucketLabel(v, bucketFor(timeWindow))}
                      formatter={(v) => formatNumber(v)}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="schedule_rows" stroke="#319795" name="Schedule rows" dot={{ r: 3 }} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>

          <Box>
            <Heading size="sm" mb={2}>Document edits</Heading>
            {series.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No activity in this window.</Text>
            ) : (
              <Box h="280px" bg="white" p={2} borderRadius="md" borderWidth={1}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tickFormatter={(v) => formatBucketLabel(v, bucketFor(timeWindow))} />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      labelFormatter={(v) => formatBucketLabel(v, bucketFor(timeWindow))}
                      formatter={(v) => formatNumber(v)}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="document_edits" stroke="#3182CE" name="Document edits" dot={{ r: 3 }} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>

          <Box>
            <Heading size="sm" mb={2}>Recordings</Heading>
            {series.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No activity in this window.</Text>
            ) : (
              <Box h="280px" bg="white" p={2} borderRadius="md" borderWidth={1}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tickFormatter={(v) => formatBucketLabel(v, bucketFor(timeWindow))} />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      labelFormatter={(v) => formatBucketLabel(v, bucketFor(timeWindow))}
                      formatter={(v) => formatNumber(v)}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="audio_records" stroke="#E53E3E" name="Recordings" dot={{ r: 3 }} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>

          <Box>
            <Heading size="sm" mb={2}>AI queries</Heading>
            {series.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No activity in this window.</Text>
            ) : (
              <Box h="280px" bg="white" p={2} borderRadius="md" borderWidth={1}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tickFormatter={(v) => formatBucketLabel(v, bucketFor(timeWindow))} />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      labelFormatter={(v) => formatBucketLabel(v, bucketFor(timeWindow))}
                      formatter={(v) => formatNumber(v)}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="ai_queries" stroke="#DD6B20" name="AI queries" dot={{ r: 3 }} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>

          <Box>
            <Heading size="sm" mb={2}>Top pages visited</Heading>
            {(data.pages || []).length === 0 ? (
              <Text fontSize="sm" color="gray.500">No page-visit data.</Text>
            ) : (
              <Box overflowX="auto">
                <Table size="sm" variant="striped">
                  <Thead>
                    <Tr>
                      <Th>Page</Th>
                      <Th isNumeric>Visits</Th>
                      <Th isNumeric>Total time</Th>
                      <Th isNumeric>Avg time</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.pages.map((p) => (
                      <Tr key={p.page}>
                        <Td>{p.page}</Td>
                        <Td isNumeric>{formatNumber(p.visits)}</Td>
                        <Td isNumeric>{formatMs(p.total_ms)}</Td>
                        <Td isNumeric>{formatMs(p.avg_ms)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </>
      ) : null}
    </VStack>
  );
}
